import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Save, ArrowLeft, Plus, Trash2, GripVertical, ChevronDown, ChevronUp,
  BookOpen, User, Award, HelpCircle, Star, MessageCircle, Loader2,
  DollarSign, Clock, Video, Image, FileText, Check, X, Eye, FolderOpen, List,
  Play, ArrowUp, ArrowDown, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MediaLibraryPicker } from '@/components/admin/MediaLibraryPicker';

const API = process.env.REACT_APP_BACKEND_URL;

// Collapsible Section Component
const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-card border border-border/50 rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-primary" />
          <span className="font-semibold text-foreground">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {isOpen && <div className="p-4 pt-0 border-t border-border/30">{children}</div>}
    </div>
  );
};

// Input Field Component
const Field = ({ label, value, onChange, type = "text", placeholder, textarea = false, rows = 3 }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-foreground mb-2">{label}</label>
    {textarea ? (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground resize-none"
      />
    ) : (
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground"
      />
    )}
  </div>
);

// Image Field with Media Library Picker
const ImageField = ({ label, value, onChange }) => {
  const [showPicker, setShowPicker] = useState(false);
  
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-foreground mb-2">{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Image URL or select from library"
          className="flex-1 px-4 py-3 bg-background border border-border rounded-xl text-foreground"
        />
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="px-4 py-3 bg-primary/20 hover:bg-primary/30 text-primary rounded-xl flex items-center gap-2 transition-colors"
        >
          <FolderOpen className="w-4 h-4" />
          <span className="text-sm font-medium">Browse</span>
        </button>
      </div>
      {value && (
        <div className="mt-2 relative w-32 h-20 rounded-lg overflow-hidden bg-zinc-800">
          <img loading="lazy" decoding="async" 
            src={value.startsWith('http') ? value : `${API}${value}`} 
            alt="" 
            className="w-full h-full object-cover" 
          />
        </div>
      )}
      <MediaLibraryPicker
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={(img) => {
          onChange(img.url);
          setShowPicker(false);
        }}
        allowedTypes={['image']}
        multiple={false}
      />
    </div>
  );
};

// List Editor Component
const ListEditor = ({ items, onChange, placeholder }) => {
  const addItem = () => onChange([...items, '']);
  const removeItem = (index) => onChange(items.filter((_, i) => i !== index));
  const updateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    onChange(newItems);
  };

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex gap-2">
          <input
            type="text"
            value={item}
            onChange={(e) => updateItem(index, e.target.value)}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm"
          />
          <button
            onClick={() => removeItem(index)}
            className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        onClick={addItem}
        className="flex items-center gap-2 text-sm text-primary hover:text-primary/80"
      >
        <Plus className="w-4 h-4" /> Add Item
      </button>
    </div>
  );
};

export const AdminCourseEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [courseData, setCourseData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [coursesList, setCoursesList] = useState([]);
  const [showCourseSelector, setShowCourseSelector] = useState(false);
  
  // Tabs and chapters
  const [activeTab, setActiveTab] = useState('details');
  const [chapters, setChapters] = useState([]);
  const [expandedChapter, setExpandedChapter] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);

  // Get courseId from URL params, default to null (show selector)
  const courseId = searchParams.get('id');

  useEffect(() => {
    fetchCoursesList();
    if (courseId) {
      fetchCourseData(courseId);
      fetchChapters(courseId);
    } else {
      setLoading(false);
      setShowCourseSelector(true);
    }
  }, [courseId]);

  const fetchCoursesList = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/courses/list`);
      setCoursesList(response.data);
    } catch (error) {
      console.error('Error fetching courses list:', error);
    }
  };

  const fetchCourseData = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/courses/${id}`);
      setCourseData(response.data);
      setShowCourseSelector(false);
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course data');
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async (id) => {
    try {
      const response = await axios.get(`${API}/api/admin/courses/${id}/chapters`);
      setChapters(response.data || []);
    } catch (error) {
      console.error('Error fetching chapters:', error);
      setChapters([]);
    }
  };

  const selectCourse = (id) => {
    navigate(`/admin/course-editor?id=${id}`);
  };

  const updateField = (field, value) => {
    setCourseData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };
  
  // Chapter management functions
  const handleAddChapter = async () => {
    const newChapter = {
      number: chapters.length + 1,
      title: 'New Chapter',
      description: 'Chapter description',
      lessons: []
    };
    
    try {
      await axios.post(`${API}/api/admin/courses/${courseId}/chapters`, newChapter);
      toast.success('Chapter added!');
      fetchChapters(courseId);
    } catch (err) {
      toast.error('Failed to add chapter');
    }
  };

  const handleUpdateChapter = async (chapterId, data) => {
    try {
      await axios.put(`${API}/api/admin/courses/${courseId}/chapters/${chapterId}`, data);
      toast.success('Chapter updated!');
      fetchChapters(courseId);
    } catch (err) {
      toast.error('Failed to update chapter');
    }
  };

  const handleDeleteChapter = async (chapterId) => {
    if (!confirm('Delete this chapter and all its lessons?')) return;
    try {
      await axios.delete(`${API}/api/admin/courses/${courseId}/chapters/${chapterId}`);
      toast.success('Chapter deleted!');
      fetchChapters(courseId);
    } catch (err) {
      toast.error('Failed to delete chapter');
    }
  };

  const handleAddLesson = (chapterId) => {
    const chapter = chapters.find(c => c.id === chapterId);
    if (!chapter) return;
    
    const newLesson = {
      title: 'New Lesson',
      description: 'Lesson description',
      video_url: '',
      duration: '5:00',
      is_preview: false
    };
    
    const updatedLessons = [...(chapter.lessons || []), newLesson];
    handleUpdateChapter(chapterId, { ...chapter, lessons: updatedLessons });
  };

  const handleUpdateLesson = (chapterId, lessonIndex, lessonData) => {
    const chapter = chapters.find(c => c.id === chapterId);
    if (!chapter) return;
    
    const updatedLessons = [...chapter.lessons];
    updatedLessons[lessonIndex] = lessonData;
    handleUpdateChapter(chapterId, { ...chapter, lessons: updatedLessons });
    setEditingLesson(null);
  };

  const handleDeleteLesson = (chapterId, lessonIndex) => {
    if (!confirm('Delete this lesson?')) return;
    const chapter = chapters.find(c => c.id === chapterId);
    if (!chapter) return;
    
    const updatedLessons = chapter.lessons.filter((_, idx) => idx !== lessonIndex);
    handleUpdateChapter(chapterId, { ...chapter, lessons: updatedLessons });
  };

  const updateNestedField = (parent, field, value) => {
    setCourseData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
    setHasChanges(true);
  };

  const updateChapter = (index, field, value) => {
    const newChapters = [...courseData.chapters];
    newChapters[index] = { ...newChapters[index], [field]: value };
    updateField('chapters', newChapters);
  };

  const updateFaq = (index, field, value) => {
    const newFaqs = [...courseData.faqs];
    newFaqs[index] = { ...newFaqs[index], [field]: value };
    updateField('faqs', newFaqs);
  };

  const addFaq = () => {
    updateField('faqs', [...courseData.faqs, { question: '', answer: '' }]);
  };

  const removeFaq = (index) => {
    updateField('faqs', courseData.faqs.filter((_, i) => i !== index));
  };

  const updateTestimonial = (index, field, value) => {
    const newTestimonials = [...courseData.testimonials];
    newTestimonials[index] = { ...newTestimonials[index], [field]: value };
    updateField('testimonials', newTestimonials);
  };

  const addTestimonial = () => {
    updateField('testimonials', [...courseData.testimonials, { name: '', role: '', text: '', rating: 5 }]);
  };

  const removeTestimonial = (index) => {
    updateField('testimonials', courseData.testimonials.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!courseId) {
      toast.error('No course selected');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/courses/${courseId}`, courseData);
      toast.success('Course saved successfully!');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  // Show course selector if no course is selected
  if (showCourseSelector || !courseData) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-card border-b border-border/50 px-6 py-4">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-white/10 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold">Course Editor</h1>
                <p className="text-sm text-muted-foreground">Select a course to edit</p>
              </div>
            </div>
          </div>
        </div>

        {/* Course List */}
        <div className="max-w-3xl mx-auto p-6">
          <div className="bg-card border border-border/50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <List className="w-6 h-6 text-primary" />
                <h2 className="text-xl font-bold">Courses & Certificates</h2>
              </div>
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  const title = prompt('Enter course title:');
                  if (!title) return;
                  try {
                    const res = await axios.post(`${API}/api/admin/courses/create?title=${encodeURIComponent(title)}&course_type=course`);
                    toast.success('Course created!');
                    fetchCoursesList();
                    selectCourse(res.data.course_id);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to create course');
                  }
                }}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-bold flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Course
              </button>
              <button
                onClick={async () => {
                  const title = prompt('Enter certificate title:');
                  if (!title) return;
                  try {
                    const res = await axios.post(`${API}/api/admin/courses/create?title=${encodeURIComponent(title)}&course_type=certificate`);
                    toast.success('Certificate created!');
                    fetchCoursesList();
                    selectCourse(res.data.course_id);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to create certificate');
                  }
                }}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black rounded-lg font-bold flex items-center gap-2"
              >
                <Award className="w-4 h-4" />
                New Certificate
              </button>
            </div>
          </div>
            
          {coursesList.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No courses found. Create one first.
            </p>
          ) : (
            <div className="space-y-3">
              {coursesList.map((course) => (
                <button
                  key={course.id}
                  onClick={() => selectCourse(course.id)}
                  className="w-full p-4 bg-background hover:bg-white/5 border border-border/50 rounded-xl text-left transition-colors flex items-center justify-between group"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                        {course.title}
                      </h3>
                      {course.source === 'certificate' && (
                        <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">Certificate</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      ID: {course.id} • ${course.price?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                  {course.source === 'certificate' ? (
                    <Award className="w-5 h-5 text-amber-500 group-hover:text-amber-400 transition-colors" />
                  ) : (
                    <BookOpen className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  )}
                </button>
              ))}
            </div>
          )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-card border-b border-border/50 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/course-editor')}
              className="p-2 hover:bg-white/10 rounded-lg"
              title="Back to course list"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-foreground">Course Editor</h1>
              <p className="text-sm text-muted-foreground">{courseData.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/course-editor')}
              className="px-4 py-2 bg-stone-700 hover:bg-stone-600 text-foreground rounded-lg font-medium flex items-center gap-2"
            >
              <List className="w-4 h-4" />
              All Courses
            </button>
            <a
              href={`/course/${courseId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg font-medium flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Preview
            </a>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="px-6 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-bold flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {hasChanges && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
            <span className="text-yellow-500 text-sm font-medium">You have unsaved changes</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border/50 pb-2">
          <button
            onClick={() => setActiveTab('details')}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
              activeTab === 'details' 
                ? 'bg-primary/20 text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Course Details
          </button>
          <button
            onClick={() => setActiveTab('chapters')}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
              activeTab === 'chapters' 
                ? 'bg-primary/20 text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Layers className="w-4 h-4 inline mr-2" />
            Chapters & Lessons ({chapters.length})
          </button>
        </div>

        {/* Details Tab */}
        {activeTab === 'details' && (
          <>
            {/* Basic Info */}
            <Section title="Basic Information" icon={FileText} defaultOpen={true}>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Course Title" value={courseData.title} onChange={(v) => updateField('title', v)} />
            <Field label="Subtitle" value={courseData.subtitle} onChange={(v) => updateField('subtitle', v)} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Price ($)" value={courseData.price} onChange={(v) => updateField('price', v)} type="number" />
            <Field label="Original Price ($)" value={courseData.originalPrice} onChange={(v) => updateField('originalPrice', v)} type="number" />
          </div>
          <Field label="Short Description" value={courseData.shortDescription} onChange={(v) => updateField('shortDescription', v)} textarea rows={2} />
          <Field label="Full Description" value={courseData.fullDescription} onChange={(v) => updateField('fullDescription', v)} textarea rows={8} />
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Total Lessons" value={courseData.totalLessons} onChange={(v) => updateField('totalLessons', v)} type="number" />
            <Field label="Total Duration" value={courseData.totalDuration} onChange={(v) => updateField('totalDuration', v)} placeholder="e.g., 8+ Hours" />
            <Field label="Skill Level" value={courseData.skillLevel} onChange={(v) => updateField('skillLevel', v)} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Language" value={courseData.language} onChange={(v) => updateField('language', v)} />
            <Field label="Subtitles" value={courseData.subtitles} onChange={(v) => updateField('subtitles', v)} />
          </div>
        </Section>

        {/* Images */}
        <Section title="Images & Media" icon={Image}>
          <ImageField label="Main Course Image URL" value={courseData.image} onChange={(v) => updateField('image', v)} />
          <ImageField label="Header/Banner Image URL" value={courseData.headerImage} onChange={(v) => updateField('headerImage', v)} />
          <Field label="Trailer Video URL" value={courseData.trailerUrl} onChange={(v) => updateField('trailerUrl', v)} placeholder="https://player.vimeo.com/video/..." />
        </Section>

        {/* Instructor */}
        <Section title="Instructor Information" icon={User}>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Instructor Name" value={courseData.instructor} onChange={(v) => updateField('instructor', v)} />
            <Field label="Instructor Title" value={courseData.instructorTitle} onChange={(v) => updateField('instructorTitle', v)} />
          </div>
          <Field label="Instructor Bio" value={courseData.instructorBio} onChange={(v) => updateField('instructorBio', v)} textarea rows={4} />
          <div className="mt-4">
            <label className="block text-sm font-medium text-foreground mb-2">Instructor Credentials</label>
            <ListEditor 
              items={courseData.instructorCredentials || []} 
              onChange={(v) => updateField('instructorCredentials', v)}
              placeholder="Add a credential..."
            />
          </div>
        </Section>

        {/* What You'll Learn */}
        <Section title="What You'll Learn" icon={BookOpen}>
          <ListEditor 
            items={courseData.whatYouLearn || []} 
            onChange={(v) => updateField('whatYouLearn', v)}
            placeholder="Add a learning outcome..."
          />
        </Section>

        {/* Chapters */}
        <Section title="Course Chapters" icon={Video}>
          {courseData.chapters?.map((chapter, index) => (
            <div key={index} className="bg-background border border-border/50 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center text-primary font-bold">
                  {chapter.number}
                </div>
                <div className="flex-1">
                  <input
                    value={chapter.title}
                    onChange={(e) => updateChapter(index, 'title', e.target.value)}
                    className="w-full bg-transparent text-foreground font-semibold text-lg border-none outline-none"
                    placeholder="Chapter Title"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <Field label="Short Description" value={chapter.shortDesc} onChange={(v) => updateChapter(index, 'shortDesc', v)} />
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Lessons" value={chapter.lessons} onChange={(v) => updateChapter(index, 'lessons', parseInt(v) || 0)} type="number" />
                  <Field label="Duration" value={chapter.duration} onChange={(v) => updateChapter(index, 'duration', v)} placeholder="45 min" />
                </div>
              </div>
              <Field label="Full Description" value={chapter.description} onChange={(v) => updateChapter(index, 'description', v)} textarea rows={2} />
              <Field label="Video URL" value={chapter.videoUrl} onChange={(v) => updateChapter(index, 'videoUrl', v)} placeholder="https://player.vimeo.com/video/..." />
              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-2">Topics Covered</label>
                <ListEditor 
                  items={chapter.topics || []} 
                  onChange={(v) => updateChapter(index, 'topics', v)}
                  placeholder="Add a topic..."
                />
              </div>
            </div>
          ))}
        </Section>

        {/* Certificate */}
        <Section title="Certificate Information" icon={Award}>
          <Field 
            label="Certificate Title" 
            value={courseData.certificateInfo?.title} 
            onChange={(v) => updateNestedField('certificateInfo', 'title', v)} 
          />
          <Field 
            label="Certificate Description" 
            value={courseData.certificateInfo?.description} 
            onChange={(v) => updateNestedField('certificateInfo', 'description', v)} 
            textarea 
            rows={3} 
          />
          <div className="grid md:grid-cols-2 gap-6 mt-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Benefits</label>
              <ListEditor 
                items={courseData.certificateInfo?.benefits || []} 
                onChange={(v) => updateNestedField('certificateInfo', 'benefits', v)}
                placeholder="Add a benefit..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Requirements</label>
              <ListEditor 
                items={courseData.certificateInfo?.requirements || []} 
                onChange={(v) => updateNestedField('certificateInfo', 'requirements', v)}
                placeholder="Add a requirement..."
              />
            </div>
          </div>
        </Section>

        {/* FAQs */}
        <Section title="Frequently Asked Questions" icon={HelpCircle}>
          {courseData.faqs?.map((faq, index) => (
            <div key={index} className="bg-background border border-border/50 rounded-xl p-4 mb-3">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <input
                    value={faq.question}
                    onChange={(e) => updateFaq(index, 'question', e.target.value)}
                    className="w-full bg-transparent text-foreground font-medium border-none outline-none mb-2"
                    placeholder="Question"
                  />
                  <textarea
                    value={faq.answer}
                    onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                    className="w-full bg-background/50 text-muted-foreground text-sm border border-border/30 rounded-lg p-2 resize-none"
                    placeholder="Answer"
                    rows={2}
                  />
                </div>
                <button onClick={() => removeFaq(index)} className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          <button onClick={addFaq} className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 mt-2">
            <Plus className="w-4 h-4" /> Add FAQ
          </button>
        </Section>

        {/* Testimonials */}
        <Section title="Student Testimonials" icon={Star}>
          {courseData.testimonials?.map((testimonial, index) => (
            <div key={index} className="bg-background border border-border/50 rounded-xl p-4 mb-3">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <input
                      value={testimonial.name}
                      onChange={(e) => updateTestimonial(index, 'name', e.target.value)}
                      className="bg-background/50 text-foreground border border-border/30 rounded-lg px-3 py-2 text-sm"
                      placeholder="Student Name"
                    />
                    <input
                      value={testimonial.role}
                      onChange={(e) => updateTestimonial(index, 'role', e.target.value)}
                      className="bg-background/50 text-foreground border border-border/30 rounded-lg px-3 py-2 text-sm"
                      placeholder="Role/Location"
                    />
                  </div>
                  <textarea
                    value={testimonial.text}
                    onChange={(e) => updateTestimonial(index, 'text', e.target.value)}
                    className="w-full bg-background/50 text-muted-foreground text-sm border border-border/30 rounded-lg p-2 resize-none"
                    placeholder="Testimonial text..."
                    rows={2}
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-muted-foreground">Rating:</span>
                    {[1,2,3,4,5].map(star => (
                      <button
                        key={star}
                        onClick={() => updateTestimonial(index, 'rating', star)}
                        className={`${star <= testimonial.rating ? 'text-yellow-500' : 'text-muted-foreground'}`}
                      >
                        <Star className="w-4 h-4 fill-current" />
                      </button>
                    ))}
                  </div>
                </div>
                <button onClick={() => removeTestimonial(index)} className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          <button onClick={addTestimonial} className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 mt-2">
            <Plus className="w-4 h-4" /> Add Testimonial
          </button>
        </Section>

        {/* Save Button at Bottom */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="px-8 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-bold flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
          </>
        )}

        {/* Chapters Tab */}
        {activeTab === 'chapters' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Course Chapters & Lessons</h2>
              <button
                onClick={handleAddChapter}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-bold flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Chapter
              </button>
            </div>

            {chapters.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border/50 rounded-xl">
                <Layers className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No chapters yet. Add your first chapter to get started.</p>
                <button
                  onClick={handleAddChapter}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-medium"
                >
                  <Plus className="w-4 h-4 inline mr-2" />
                  Add First Chapter
                </button>
              </div>
            ) : (
              chapters.map((chapter, chapterIndex) => (
                <div key={chapter.id} className="bg-card border border-border/50 rounded-xl overflow-hidden">
                  {/* Chapter Header */}
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5"
                    onClick={() => setExpandedChapter(expandedChapter === chapter.id ? null : chapter.id)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-primary/20 text-primary rounded-lg flex items-center justify-center font-bold text-sm">
                        {chapter.number || chapterIndex + 1}
                      </span>
                      <div>
                        <h3 className="font-semibold text-foreground">{chapter.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {chapter.lessons?.length || 0} lessons
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteChapter(chapter.id); }}
                        className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {expandedChapter === chapter.id ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>

                  {/* Chapter Content (Expanded) */}
                  {expandedChapter === chapter.id && (
                    <div className="border-t border-border/50 p-4">
                      {/* Chapter Edit Fields */}
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Chapter Title</label>
                          <input
                            type="text"
                            value={chapter.title}
                            onChange={(e) => handleUpdateChapter(chapter.id, { ...chapter, title: e.target.value })}
                            className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Chapter Number</label>
                          <input
                            type="number"
                            value={chapter.number || chapterIndex + 1}
                            onChange={(e) => handleUpdateChapter(chapter.id, { ...chapter, number: parseInt(e.target.value) })}
                            className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                          />
                        </div>
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">Description</label>
                        <textarea
                          value={chapter.description || ''}
                          onChange={(e) => handleUpdateChapter(chapter.id, { ...chapter, description: e.target.value })}
                          className="w-full px-3 py-2 bg-background border border-border rounded-lg resize-none"
                          rows={2}
                        />
                      </div>

                      {/* Lessons */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-sm text-muted-foreground">LESSONS</h4>
                          <button
                            onClick={() => handleAddLesson(chapter.id)}
                            className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" /> Add Lesson
                          </button>
                        </div>

                        {chapter.lessons?.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">No lessons in this chapter</p>
                        ) : (
                          <div className="space-y-2">
                            {chapter.lessons?.map((lesson, lessonIndex) => (
                              <div key={lessonIndex} className="bg-background border border-border/50 rounded-lg p-3">
                                {editingLesson === `${chapter.id}-${lessonIndex}` ? (
                                  /* Editing Mode */
                                  <div className="space-y-3">
                                    <input
                                      type="text"
                                      value={lesson.title}
                                      onChange={(e) => {
                                        const updated = { ...lesson, title: e.target.value };
                                        const updatedLessons = [...chapter.lessons];
                                        updatedLessons[lessonIndex] = updated;
                                        setChapters(prev => prev.map(c => c.id === chapter.id ? { ...c, lessons: updatedLessons } : c));
                                      }}
                                      className="w-full px-3 py-2 bg-card border border-border rounded-lg text-sm"
                                      placeholder="Lesson Title"
                                    />
                                    <textarea
                                      value={lesson.description || ''}
                                      onChange={(e) => {
                                        const updated = { ...lesson, description: e.target.value };
                                        const updatedLessons = [...chapter.lessons];
                                        updatedLessons[lessonIndex] = updated;
                                        setChapters(prev => prev.map(c => c.id === chapter.id ? { ...c, lessons: updatedLessons } : c));
                                      }}
                                      className="w-full px-3 py-2 bg-card border border-border rounded-lg text-sm resize-none"
                                      placeholder="Lesson Description"
                                      rows={2}
                                    />
                                    <div className="grid grid-cols-2 gap-3">
                                      <input
                                        type="text"
                                        value={lesson.video_url || ''}
                                        onChange={(e) => {
                                          const updated = { ...lesson, video_url: e.target.value };
                                          const updatedLessons = [...chapter.lessons];
                                          updatedLessons[lessonIndex] = updated;
                                          setChapters(prev => prev.map(c => c.id === chapter.id ? { ...c, lessons: updatedLessons } : c));
                                        }}
                                        className="px-3 py-2 bg-card border border-border rounded-lg text-sm"
                                        placeholder="Video URL"
                                      />
                                      <input
                                        type="text"
                                        value={lesson.duration || ''}
                                        onChange={(e) => {
                                          const updated = { ...lesson, duration: e.target.value };
                                          const updatedLessons = [...chapter.lessons];
                                          updatedLessons[lessonIndex] = updated;
                                          setChapters(prev => prev.map(c => c.id === chapter.id ? { ...c, lessons: updatedLessons } : c));
                                        }}
                                        className="px-3 py-2 bg-card border border-border rounded-lg text-sm"
                                        placeholder="Duration (e.g., 5:30)"
                                      />
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <label className="flex items-center gap-2 text-sm">
                                        <input
                                          type="checkbox"
                                          checked={lesson.is_preview || false}
                                          onChange={(e) => {
                                            const updated = { ...lesson, is_preview: e.target.checked };
                                            const updatedLessons = [...chapter.lessons];
                                            updatedLessons[lessonIndex] = updated;
                                            setChapters(prev => prev.map(c => c.id === chapter.id ? { ...c, lessons: updatedLessons } : c));
                                          }}
                                          className="rounded"
                                        />
                                        Free Preview
                                      </label>
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => setEditingLesson(null)}
                                          className="px-3 py-1 text-sm text-muted-foreground hover:text-foreground"
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          onClick={() => handleUpdateLesson(chapter.id, lessonIndex, chapter.lessons[lessonIndex])}
                                          className="px-3 py-1 bg-primary text-primary-foreground rounded-lg text-sm"
                                        >
                                          Save
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  /* View Mode */
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <Play className="w-4 h-4 text-primary" />
                                      <div>
                                        <p className="font-medium text-sm">{lesson.title}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {lesson.duration || 'No duration'} 
                                          {lesson.is_preview && <span className="ml-2 text-green-500">• Free Preview</span>}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => setEditingLesson(`${chapter.id}-${lessonIndex}`)}
                                        className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-white/10 rounded"
                                      >
                                        <FileText className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteLesson(chapter.id, lessonIndex)}
                                        className="p-1.5 text-red-500 hover:bg-red-500/10 rounded"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCourseEditor;
